import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import AddIcon from '@mui/icons-material/Add'
import { Alert, Button, ButtonGroup, Stack } from '@mui/material'
import { DeleteButton, Editable } from '~/components'
import { useCanEdit } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'

const EmpCoTable = () => {
  const { t } = useTranslation()
  const canEdit = useCanEdit()
  const dispatch = useDispatch()
  const { brand } = useRouteContext(EmpCoTable.name)
  const brandUrl = brand?.url

  const empCo = useSelector((state) =>
    bshSlice.empCo(state, { skipCount: true })
  )

  const hasEmptyRow = useMemo(() => {
    return empCo.some((row) => row.target === '')
  }, [empCo])

  const [showEmptyRowError, setShowEmptyRowError] = useState(false)

  const persistRow = useCallback(
    async (row, patch) => {
      if (showEmptyRowError) {
        setShowEmptyRowError(false)
      }
      const updated = await dispatch(
        bshSlice.saveEmpCo({ data: { brandUrl, id: row.id, ...patch } })
      )
      return !!updated?.payload
    },
    [brandUrl, dispatch, showEmptyRowError]
  )

  const addRow = useCallback(async () => {
    if (hasEmptyRow) {
      setShowEmptyRowError(true)
      return
    }
    if (showEmptyRowError) {
      setShowEmptyRowError(false)
    }
    await dispatch(
      bshSlice.createEmpCo({
        data: { brandUrl, target: '', replacement: '' },
      })
    )
  }, [brandUrl, dispatch, hasEmptyRow, showEmptyRowError])

  const removeRow = useCallback(
    async (row) => {
      await dispatch(bshSlice.removeEmpCo({ data: { brandUrl, id: row.id } }))
    },
    [brandUrl, dispatch]
  )

  if (!brandUrl) {
    return null
  }

  return (
    <Stack spacing={2}>
      {empCo.map((row) => (
        <Stack direction="row" key={row.id} spacing={1}>
          <Stack
            direction="row"
            sx={{ flexGrow: 1, gap: '8px', minWidth: '1px' }}
            useFlexGap
          >
            <Editable
              placeholder={t('empco.target-placeholder')}
              sx={{ width: 'calc(50% - 4px)' }}
              value={row.target ?? ''}
              variant="name"
              onStore={(target) => persistRow(row, { target })}
            />
            <Editable
              placeholder={t('empco.replacement-placeholder')}
              sx={{ width: 'calc(50% - 4px)' }}
              value={row.replacement ?? ''}
              variant="value"
              onStore={(replacement) => persistRow(row, { replacement })}
            />
          </Stack>
          {canEdit && (
            <ButtonGroup
              sx={{
                alignItems: 'center',
              }}
            >
              <DeleteButton
                dialog={{
                  title: t('empco.delete.confirm.title'),
                  body: t('empco.delete.confirm.body'),
                }}
                title={t('empco.delete.cta')}
                onConfirmed={async () => {
                  await removeRow(row)
                  return true
                }}
              />
            </ButtonGroup>
          )}
        </Stack>
      ))}
      {showEmptyRowError && (
        <Alert severity="error" variant="filled">
          {t('empco.empty-row.error')}
        </Alert>
      )}
      {canEdit && (
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => {
            addRow()
          }}
        >
          {t('empco.add-row')}
        </Button>
      )}
    </Stack>
  )
}

EmpCoTable.propTypes = {}

export default EmpCoTable
